/* Paw Pet product page — Design 1 */
(() => {
  'use strict';

  if (window.PawPetProductDesign1Loaded) return;
  window.PawPetProductDesign1Loaded = true;

  const SECTION_SELECTOR = '.pawpet-product-design1';
  const MANUAL_TARGET_SELECTOR = '.pawpet-subscription-target';
  const SEAL_TARGET_SELECTOR = '.sealsubs-target-element';
  const SEAL_WIDGET_SELECTOR = [
    '.sls-option-container',
    '[class*="sls-option-container"]',
    '.sls-widget-container',
    '[class*="sls-widget"]',
    '[data-seal-subscriptions-widget]'
  ].join(',');

  const sealTimers = new WeakMap();

  function containsSealWidget(element) {
    return Boolean(element && element.querySelector(SEAL_WIDGET_SELECTOR));
  }

  function moveSealWidget(section, manualTarget, preferredSource) {
    if (!section || !manualTarget || containsSealWidget(manualTarget)) {
      return containsSealWidget(manualTarget);
    }

    const sources = [];
    const preferredTarget = preferredSource instanceof Element
      ? preferredSource.closest(SEAL_TARGET_SELECTOR)
      : null;

    if (preferredTarget && preferredTarget !== manualTarget) {
      sources.push(preferredTarget);
    }

    section.querySelectorAll(SEAL_TARGET_SELECTOR).forEach((candidate) => {
      if (candidate !== manualTarget && !sources.includes(candidate)) {
        sources.push(candidate);
      }
    });

    const productId = manualTarget.dataset.productId;
    if (productId) {
      document.querySelectorAll(`${SEAL_TARGET_SELECTOR}[data-product-id="${productId}"]`).forEach((candidate) => {
        if (candidate !== manualTarget && !sources.includes(candidate)) {
          sources.push(candidate);
        }
      });
    }

    const source = sources.find(containsSealWidget);
    if (!source) return false;

    while (source.firstChild) {
      manualTarget.appendChild(source.firstChild);
    }

    if (source.parentNode) {
      source.remove();
    }

    return containsSealWidget(manualTarget);
  }

  function cleanDuplicateSealTargets(section, manualTarget) {
    section.querySelectorAll(SEAL_TARGET_SELECTOR).forEach((candidate) => {
      if (candidate === manualTarget) return;
      if (!candidate.children.length && !candidate.textContent.trim()) {
        candidate.remove();
      }
    });
  }

  function getSealRows(target) {
    if (!target) return [];

    return [...target.querySelectorAll('.sls-option-container, [class*="sls-option-container"]')]
      .filter((row) => !row.parentElement?.closest('.sls-option-container, [class*="sls-option-container"]'));
  }

  function classifySealRows(target) {
    getSealRows(target).forEach((row) => {
      const text = row.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      const checked = Boolean(row.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked'));
      const isOneTime = /one[\s-]?time|single purchase|pay once/.test(text);
      const isSubscription = /subscribe|subscription|regular delivery|selling plan/.test(text) && !isOneTime;

      row.classList.toggle('pawpet-seal-option--one-time', isOneTime);
      row.classList.toggle('pawpet-seal-option--subscription', isSubscription);
      row.classList.toggle('pawpet-seal-option--selected', checked);
    });
  }

  function normaliseSealCopy(target) {
    if (!target) return;

    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    nodes.forEach((node) => {
      const original = node.nodeValue || '';
      let replacement = original;

      replacement = replacement.replace(/one\s*time\s*purchase/gi, 'One-time purchase');
      replacement = replacement.replace(/subscribe\s+and\s+save/gi, 'Subscribe & Save');

      if (replacement !== original) {
        node.nodeValue = replacement;
      }
    });
  }

  function setSubscriptionPanelState(section, preferredSource) {
    const target = section.querySelector(MANUAL_TARGET_SELECTOR);
    const panel = section.querySelector('[data-pawpet-subscription-panel]');

    if (!target || !panel) return false;

    moveSealWidget(section, target, preferredSource);
    cleanDuplicateSealTargets(section, target);

    const ready = containsSealWidget(target);
    panel.hidden = !ready;
    panel.classList.toggle('is-active', ready);

    if (ready) {
      normaliseSealCopy(target);
      classifySealRows(target);
    }

    return ready;
  }

  function queueSealSync(section, preferredSource) {
    if (!section) return;

    const previous = sealTimers.get(section);
    if (previous) window.clearTimeout(previous);

    const timer = window.setTimeout(() => {
      sealTimers.delete(section);
      setSubscriptionPanelState(section, preferredSource);
    }, 35);

    sealTimers.set(section, timer);
  }

  function initSeal(section) {
    const target = section.querySelector(MANUAL_TARGET_SELECTOR);
    if (!target || target.dataset.pawpetSealReady === 'true') return;

    target.dataset.pawpetSealReady = 'true';

    const observer = new MutationObserver(() => {
      queueSealSync(section);
    });

    observer.observe(section, {
      childList: true,
      subtree: true,
      characterData: true
    });

    target.addEventListener('change', () => {
      window.setTimeout(() => classifySealRows(target), 0);
    });

    setSubscriptionPanelState(section);

    if (window.SealSubs && typeof window.SealSubs.refresh === 'function') {
      try {
        window.SealSubs.refresh();
      } catch (error) {
        console.warn('Paw Pet: Seal Subscriptions refresh was not available.', error);
      }
    }

    [120, 350, 700, 1300, 2400, 4500, 7500].forEach((delay) => {
      window.setTimeout(() => queueSealSync(section), delay);
    });
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(amount);
    } catch (_error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function initDeliveryProgress(section) {
    const progress = section.querySelector('[data-pawpet-delivery-progress]');
    if (!progress || progress.dataset.pawpetDeliveryReady === 'true') return;

    progress.dataset.pawpetDeliveryReady = 'true';

    const threshold = Number.parseInt(progress.dataset.threshold || '0', 10);
    const cartTotal = Number.parseInt(progress.dataset.cartTotal || '0', 10);
    const currency = progress.dataset.currency || 'GBP';
    const variantsScript = progress.querySelector('[data-pawpet-product-variants]');
    let variants = [];

    try {
      variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    } catch (error) {
      console.warn('Paw Pet: product variants could not be read for the delivery bar.', error);
    }

    const variantPrices = new Map(variants.map((variant) => [String(variant.id), Number(variant.price)]));

    const update = () => {
      if (threshold <= 0) {
        progress.hidden = true;
        return;
      }

      const variantInput = section.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      const quantityInput =
        section.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]') ||
        section.querySelector('.pawpet-quantity-block input[name="quantity"]');
      const variantId = variantInput?.value;
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
      const fallbackPrice = Number.parseInt(progress.dataset.initialVariantPrice || '0', 10);
      const variantPrice = variantPrices.get(String(variantId)) ?? fallbackPrice;
      const projectedTotal = Math.max(0, cartTotal + (variantPrice * quantity));
      const remaining = Math.max(0, threshold - projectedTotal);
      const percentage = Math.min(100, Math.max(0, (projectedTotal / threshold) * 100));

      const message = progress.querySelector('[data-pawpet-delivery-message]');
      const current = progress.querySelector('[data-pawpet-delivery-current]');
      const fill = progress.querySelector('[data-pawpet-delivery-fill]');
      const track = progress.querySelector('[data-pawpet-delivery-track]');

      if (message) {
        message.textContent = remaining === 0
          ? "You've unlocked FREE DELIVERY"
          : `Add ${formatMoney(remaining, currency)} more for FREE DELIVERY`;
      }

      if (current) {
        current.textContent = formatMoney(projectedTotal, currency);
      }

      if (fill) {
        fill.style.setProperty('--pawpet-delivery-progress', `${percentage}%`);
      }

      if (track) {
        track.setAttribute('aria-valuenow', String(Math.min(projectedTotal, threshold)));
      }
    };

    section.addEventListener('change', () => {
      [0, 60, 220].forEach((delay) => window.setTimeout(update, delay));
    });

    section.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') {
        update();
      }
    });

    update();
  }

  function normaliseAddToBasket(section) {
    const button = section.querySelector('.product-form__submit[name="add"]');
    const label = button?.querySelector(':scope > span');
    if (!button || !label) return;

    const current = label.textContent.replace(/\s+/g, ' ').trim();
    if (/^add to (cart|bag)$/i.test(current)) {
      label.textContent = 'Add to basket';
    }
  }

  function watchAddToBasket(section) {
    const button = section.querySelector('.product-form__submit[name="add"]');
    if (!button || button.dataset.pawpetButtonReady === 'true') return;

    button.dataset.pawpetButtonReady = 'true';
    normaliseAddToBasket(section);

    const observer = new MutationObserver(() => normaliseAddToBasket(section));
    observer.observe(button, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });
  }

  function initBrandLogos(section) {
    section.querySelectorAll('[data-pawpet-brand-logo]').forEach((image) => {
      if (image.dataset.pawpetBrandLogoReady === 'true') return;
      image.dataset.pawpetBrandLogoReady = 'true';

      const card = image.closest('[data-pawpet-brand-card]');
      const fallbacks = [
        image.dataset.fallbackAsset,
        image.dataset.fallbackFile
      ].filter(Boolean);

      let fallbackIndex = 0;

      const showLogo = () => {
        card?.classList.remove('pawpet-product-brand-card--no-logo');
      };

      const tryNextLogo = () => {
        const next = fallbacks[fallbackIndex];
        fallbackIndex += 1;

        if (next && next !== image.currentSrc && next !== image.src) {
          image.src = next;
          return;
        }

        if (fallbackIndex < fallbacks.length) {
          tryNextLogo();
          return;
        }

        image.remove();
        card?.classList.add('pawpet-product-brand-card--no-logo');
      };

      image.addEventListener('load', showLogo);
      image.addEventListener('error', tryNextLogo);

      if (image.complete) {
        if (image.naturalWidth > 0) {
          showLogo();
        } else {
          tryNextLogo();
        }
      }
    });
  }

  function refreshSealAfterVariantChange(section) {
    if (!window.SealSubs || typeof window.SealSubs.refresh !== 'function') return;

    try {
      window.SealSubs.refresh();
    } catch (error) {
      console.warn('Paw Pet: Seal Subscriptions could not refresh after the variant changed.', error);
    }

    [80, 250, 650, 1200].forEach((delay) => {
      window.setTimeout(() => queueSealSync(section), delay);
    });
  }

  function watchVariantChanges(section) {
    if (section.dataset.pawpetVariantWatchReady === 'true') return;
    section.dataset.pawpetVariantWatchReady = 'true';

    section.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      const isVariantControl =
        target.matches('variant-radios input, variant-selects select') ||
        target.name === 'id';

      if (!isVariantControl) return;
      window.setTimeout(() => refreshSealAfterVariantChange(section), 40);
    });
  }

  function initDescriptionPosition(section) {
    const description = section.querySelector('[data-pawpet-product-description]');
    const desktopTarget = section.querySelector('[data-pawpet-description-desktop-target]');
    const origin = section.querySelector('[data-pawpet-description-origin]');

    if (!description || !desktopTarget || !origin || description.dataset.pawpetDescriptionReady === 'true') {
      return;
    }

    description.dataset.pawpetDescriptionReady = 'true';
    const desktopQuery = window.matchMedia('(min-width: 1025px)');

    const positionDescription = () => {
      if (desktopQuery.matches) {
        if (description.parentElement !== desktopTarget) {
          desktopTarget.appendChild(description);
        }
      } else if (origin.parentElement && description.previousElementSibling !== origin) {
        origin.insertAdjacentElement('afterend', description);
      }
    };

    positionDescription();

    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', positionDescription);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(positionDescription);
    }
  }

  function initSection(section) {
    if (!section) return;

    initBrandLogos(section);
    initDeliveryProgress(section);
    initSeal(section);
    watchVariantChanges(section);
    watchAddToBasket(section);
    initDescriptionPosition(section);
  }

  function initAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initSection(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  }

  function findSectionFromEvent(event) {
    const eventElement = event.detail?.element instanceof Element
      ? event.detail.element
      : event.target instanceof Element
        ? event.target
        : null;

    return eventElement?.closest(SECTION_SELECTOR) || document.querySelector(SECTION_SELECTOR);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('sealsubs:subscription_widget_created', (event) => {
    const section = findSectionFromEvent(event);
    if (section) queueSealSync(section, event.detail?.element || event.target);
  });

  document.addEventListener('sealsubs:selling_plan_changed', (event) => {
    const section = findSectionFromEvent(event);
    if (section) queueSealSync(section, event.detail?.element || event.target);
  });
})();
